import React, { useState, useRef } from 'react';
import axios from 'axios';
import { Upload, Trash2, Image as ImageIcon, Maximize2, Move } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BackgroundLayersEditor = ({
  layers = [],
  token,
  editingPage = 'main',
  onLayersChange,
  onUploaded,
}) => {
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState('');
  const [pendingDelete, setPendingDelete] = useState(null); // { type: 'one'|'all', filename? }
  const fileInputRef = useRef(null);
  const authHeaders = { Authorization: `Bearer ${token}` };
  const requestCfg = { headers: authHeaders, params: { page: editingPage } };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { setMessage('Xəta: Yalnız şəkil'); return; }
    if (file.size > 25 * 1024 * 1024) { setMessage('Xəta: Maks 25MB'); return; }

    setUploading(true);
    setMessage('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/admin/background`, formData, {
        headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' },
        params: { page: editingPage },
      });
      const newLayer = res.data.layer;
      onLayersChange([...(layers || []), newLayer]);
      if (onUploaded) onUploaded(res.data.filename);
      setMessage('Layer əlavə edildi!');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setMessage('Xəta: ' + (err.response?.data?.detail || 'Yükləmə uğursuz'));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const performDeleteOne = async (filename) => {
    const optimistic = (layers || []).filter(l => l.filename !== filename);
    onLayersChange(optimistic);
    try {
      const res = await axios.delete(`${API}/admin/background/${encodeURIComponent(filename)}`, requestCfg);
      onLayersChange(res.data.layers || []);
      setMessage('Layer silindi');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      onLayersChange(layers || []);
      const detail = err.response?.data?.detail || err.message || 'Silinmə uğursuz';
      setMessage('Xəta: ' + detail);
    } finally {
      setPendingDelete(null);
    }
  };

  const performDeleteAll = async () => {
    const previous = layers || [];
    onLayersChange([]);
    try {
      await axios.delete(`${API}/admin/background`, requestCfg);
      setMessage('Bütün layer-lər silindi');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      onLayersChange(previous);
      const detail = err.response?.data?.detail || err.message || 'Silinmə uğursuz';
      setMessage('Xəta: ' + detail);
    } finally {
      setPendingDelete(null);
    }
  };

  const handleDeleteLayer = (filename) => setPendingDelete({ type: 'one', filename });
  const handleDeleteAll = () => setPendingDelete({ type: 'all' });

  const updateLayer = (idx, changes) => {
    const next = layers.map((l, i) => i === idx ? { ...l, ...changes } : l);
    onLayersChange(next);
  };

  const moveLayer = (idx, delta) => {
    const newIdx = idx + delta;
    if (newIdx < 0 || newIdx >= layers.length) return;
    const next = [...layers];
    [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
    onLayersChange(next);
  };

  return (
    <div className="admin-section" data-testid="bg-layers-section">
      <div className="flex items-center justify-between mb-2">
        <h3 className="admin-section-title mb-0">Arxa fon layer-ləri</h3>
        <div className="flex gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
            data-testid="bg-file-input"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="admin-btn-primary"
            data-testid="bg-upload-btn"
          >
            <Upload size={16} /> {uploading ? 'Yüklənir...' : 'Yeni layer'}
          </button>
          {layers.length > 0 && (
            <button
              onClick={handleDeleteAll}
              className="admin-btn-secondary text-red-400"
              data-testid="bg-delete-all-btn"
            >
              <Trash2 size={16} /> Hamısı
            </button>
          )}
        </div>
      </div>
      <p className="admin-section-desc">
        Çoxlu şəkil yükləyin. Hər biri ayrıca miqyas, ofset, şəffaflıqla tənzimlənir.
        Siyahıdakı ardıcıllıq = ekranda yığılma sırası (üst = daha üstdə). Maks 25MB.
      </p>

      {message && (
        <p className={`text-sm mb-3 ${message.startsWith('Xəta') ? 'text-red-400' : 'text-green-400'}`} data-testid="bg-msg">
          {message}
        </p>
      )}

      {layers.length === 0 ? (
        <div className="bg-empty-state" data-testid="bg-empty">
          <ImageIcon size={48} className="text-white/20" />
          <p className="text-white/40 text-sm mt-2">Hələ layer yoxdur. Yuxarıdakı "Yeni layer" düyməsi ilə şəkil yükləyin.</p>
        </div>
      ) : (
        <div className="bg-layers-list">
          {layers.map((layer, idx) => (
            <div key={`${layer.filename}-${idx}`} className="bg-layer-item" data-testid={`bg-layer-${idx}`}>
              <div className="bg-layer-head">
                <img
                  src={`${BACKEND_URL}/api/uploads/${layer.filename}`}
                  alt={layer.filename}
                  className="bg-layer-thumb"
                />
                <div className="bg-layer-info">
                  <div className="bg-layer-filename">Layer #{idx + 1}</div>
                  <div className="bg-layer-subname">{layer.filename}</div>
                </div>
                <div className="bg-layer-actions">
                  <button
                    onClick={() => moveLayer(idx, -1)}
                    disabled={idx === 0}
                    className="bg-layer-small-btn"
                    title="Yuxarı"
                    data-testid={`bg-move-up-${idx}`}
                  >↑</button>
                  <button
                    onClick={() => moveLayer(idx, 1)}
                    disabled={idx === layers.length - 1}
                    className="bg-layer-small-btn"
                    title="Aşağı"
                    data-testid={`bg-move-down-${idx}`}
                  >↓</button>
                  <button
                    onClick={() => handleDeleteLayer(layer.filename)}
                    className="bg-layer-small-btn text-red-400"
                    title="Sil"
                    data-testid={`bg-delete-${idx}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>

              <div className="bg-layer-controls">
                <div className="bg-layer-control">
                  <label className="bg-layer-fit-label">Ölçü rejimi</label>
                  <select
                    className="bg-layer-fit-select"
                    value={layer.fit_mode || 'cover'}
                    onChange={(e) => updateLayer(idx, { fit_mode: e.target.value })}
                    data-testid={`bg-fit-mode-${idx}`}
                  >
                    <option value="cover">Cover (ekranı doldur)</option>
                    <option value="contain">Contain (sığdır)</option>
                    <option value="stretch">Stretch (uzat)</option>
                    <option value="natural">Natural (orijinal)</option>
                  </select>
                </div>
                <div className="bg-layer-control">
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-white/70 flex items-center gap-1"><Maximize2 size={11} /> Miqyas</span>
                    <span className="font-mono text-purple-300">{(layer.scale ?? 1).toFixed(2)}x</span>
                  </div>
                  <input
                    type="range" min="0.2" max="3" step="0.05"
                    value={layer.scale ?? 1}
                    onChange={(e) => updateLayer(idx, { scale: parseFloat(e.target.value) })}
                    className="size-slider"
                    data-testid={`bg-scale-${idx}`}
                  />
                </div>
                <div className="bg-layer-control">
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-white/70 flex items-center gap-1"><Move size={11} /> X</span>
                    <span className="font-mono text-purple-300">{layer.offset_x ?? 0}px</span>
                  </div>
                  <input
                    type="range" min="-300" max="300" step="5"
                    value={layer.offset_x ?? 0}
                    onChange={(e) => updateLayer(idx, { offset_x: parseFloat(e.target.value) })}
                    className="size-slider"
                    data-testid={`bg-offset-x-${idx}`}
                  />
                </div>
                <div className="bg-layer-control">
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-white/70 flex items-center gap-1"><Move size={11} /> Y</span>
                    <span className="font-mono text-purple-300">{layer.offset_y ?? 0}px</span>
                  </div>
                  <input
                    type="range" min="-400" max="400" step="5"
                    value={layer.offset_y ?? 0}
                    onChange={(e) => updateLayer(idx, { offset_y: parseFloat(e.target.value) })}
                    className="size-slider"
                    data-testid={`bg-offset-y-${idx}`}
                  />
                </div>
                <div className="bg-layer-control">
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-white/70">Şəffaflıq</span>
                    <span className="font-mono text-purple-300">{Math.round((layer.opacity ?? 1) * 100)}%</span>
                  </div>
                  <input
                    type="range" min="0" max="1" step="0.05"
                    value={layer.opacity ?? 1}
                    onChange={(e) => updateLayer(idx, { opacity: parseFloat(e.target.value) })}
                    className="size-slider"
                    data-testid={`bg-opacity-${idx}`}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {pendingDelete && (
        <div
          className="mt-4 p-3 rounded border border-red-500/40 bg-red-500/10 flex items-center justify-between gap-3 flex-wrap"
          data-testid="bg-delete-confirm-banner"
        >
          <span className="text-sm text-red-200">
            {pendingDelete.type === 'all'
              ? 'Bütün arxa fon layer-lərini silmək istəyirsiniz?'
              : <>"<strong>{pendingDelete.filename}</strong>" layer-ini silmək istəyirsiniz?</>}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              className="admin-btn-secondary"
              onClick={() => setPendingDelete(null)}
              data-testid="bg-delete-cancel"
            >
              Ləğv et
            </button>
            <button
              type="button"
              className="admin-btn-primary"
              style={{ background: 'rgba(239,68,68,0.85)', borderColor: '#ef4444' }}
              onClick={() => pendingDelete.type === 'all'
                ? performDeleteAll()
                : performDeleteOne(pendingDelete.filename)}
              data-testid="bg-delete-confirm"
            >
              Sil
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BackgroundLayersEditor;
